import { ConsentFlow } from '@imprivata-cloud/authn';
import { submitConsent } from '../../api/selfServiceServices';
import { useErrorContext } from '../../errorHandler/context/ErrorContext';
import { AppError } from '../../errorHandler/errors';
import { useNavigate } from 'react-router';
import { Flex, Spin } from 'antd';
import { useSelfServiceData } from '../utils/useSelfServiceData';
import { type ConsentGrantType, ConsentType } from '../../api/constants';

import './styles.less';
import { SelfServicePath } from '../SelfServiceRoutes';
import { consentNeeded, getBiometricsConsentInfo } from '../utils/helpers';
import { useEffect, useMemo } from 'react';

const Consent: React.FC = () => {
  const { data, isLoading, isError, error } = useSelfServiceData();
  const { emitError } = useErrorContext();
  const navigate = useNavigate();
  const biometricConsentInfo = useMemo(() => {
    return getBiometricsConsentInfo(data);
  }, [data]);

  useEffect(() => {
    if (biometricConsentInfo && !consentNeeded(biometricConsentInfo)) {
      navigate(SelfServicePath.SELF_SERVICE_LANDING_PAGE);
    }
  }, [biometricConsentInfo, navigate]);

  if (isError) {
    if (error instanceof AppError) {
      emitError(error);
    } else {
      emitError(AppError.fromMessage('Error getting self service data'));
    }
  }

  const handleSubmitConsent = async (userConsentResponse: ConsentGrantType, stateOfResidence: string) => {
    try {
      await submitConsent({
        consentType: ConsentType.BIOMETRICS,
        residenceState: stateOfResidence,
        grantType: userConsentResponse,
        documentName: biometricConsentInfo?.documentName ?? '',
      });
      navigate(SelfServicePath.SELF_SERVICE_LANDING_PAGE);
    } catch (err: unknown) {
      if (err instanceof AppError) {
        emitError(err);
      } else {
        emitError(AppError.fromMessage('Error while submitting consent'));
      }
    }
  };

  const onError = (err: unknown) => {
    if (err instanceof AppError) {
      emitError(err);
    } else {
      emitError(AppError.fromMessage('Error while submitting consent'));
    }
  };

  const onCancel = () => {
    console.debug('Consent cancelled');
    navigate(SelfServicePath.SELF_SERVICE_LANDING_PAGE);
  };

  return (
    <div className="face-content">
      {isLoading ? (
        <Flex align="center" justify="center">
          <Spin />
        </Flex>
      ) : (
        <ConsentFlow
          modalRoot={undefined}
          onCancel={onCancel}
          consent={biometricConsentInfo}
          handleSubmitConsent={handleSubmitConsent}
          onError={onError}
        />
      )}
    </div>
  );
};

export default Consent;
